// // Makes sure Vuex works with IE11
// import 'es6-promise/auto';

import { createStore } from 'vuex'

import 'default-passive-events';

import VuexPersistence from 'vuex-persist';

/**
 * DELIBERATELY NO `reducer` AND NO `modules` FILTER. Do not add one.
 *
 * A reducer here would apply to the WHOLE store, so it would have to re-enumerate
 * every module that depends on persistence -- `current_user`, and `push`'s PREF-05
 * explainer flag, which is designed to survive both a restart and a logout -- and
 * the first module anybody registered afterwards would silently stop persisting,
 * with nothing to notice. It was considered as the fix for findings B3/C5 and
 * rejected for exactly that blast radius; the per-process fields are reset at boot
 * instead (see `push/reset_session_state` below). The reasoning is written out in
 * full at the top of src/store/modules/push.js.
 */
const vuexLocal = new VuexPersistence({
  key: 'private-gym',
  storage: window.localStorage,
});

import api from "./modules/api";
import app from "./modules/app";
import current_user from "./modules/current_user";
import notifications from "./modules/notifications";
import exercises from "./modules/exercises";
import activities from "./modules/activities";
import training_sessions from "./modules/training_sessions";
import training_slots from "./modules/training_slots";
import gyms from "./modules/gyms";
import feedback from "./modules/feedback";
import broadcasting from "./modules/broadcasting";
import news from "./modules/news";
import push from "./modules/push";
import notification_preferences from "./modules/notification_preferences";
import booking_stages from "./modules/booking_stages";
import broadcasts from "./modules/broadcasts";

export const store = createStore({

  namespaced: true,

  modules: {
    api: api,
    app: app,
    current_user: current_user,
    exercises: exercises,
    activities: activities,
    training_sessions: training_sessions,
    training_slots: training_slots,
    gyms: gyms,
    feedback: feedback,
    notifications: notifications,
    broadcasting: broadcasting,
    news: news,
    push: push,
    notification_preferences: notification_preferences,
    booking_stages: booking_stages,
    broadcasts: broadcasts,
  },

  state: {
    //
  },

  getters: {
    //
  },

  mutations: {},

  actions: {
    //
  },

  plugins: [
    vuexLocal.plugin
  ],
})

/**
 * BOOT RESET (B3/C5). Throw away the previous process's per-process push state.
 *
 * vuexLocal's synchronous plugin does its `replaceState` while it is being
 * installed, i.e. inside the `createStore` call above -- so by this line the store
 * is fully rehydrated from localStorage and nothing has read it yet. That ordering
 * is what makes one commit here sufficient.
 *
 * WHY IT IS NEEDED AT ALL. The whole store is mirrored to localStorage with no
 * reducer, so `push`'s per-process fields come back on every launch describing a
 * process that no longer exists. Phase 39's push ignition read one of them
 * (`registered`) as a guard and skipped registration entirely, which meant a cold
 * launch attached none of the four native listeners: a rotated token was never
 * observed and a tapped notification was never routed. The ignition no longer
 * guards on any such flag, and this reset is the second half of the same fix --
 * the state itself stops lying, rather than only the one caller that believed it.
 *
 * Not in the mutation list of any module but `push`, on purpose: this is the only
 * module whose per-process state was being persisted AND acted on.
 */
store.commit('push/reset_session_state');
